import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Lock, Loader2, RefreshCcw, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "anreal_admin_token";

const STATUS_OPTIONS = [
  { value: "disponivel", label: "Disponível", bg: "bg-[#2B3B31]", text: "text-[#F9F9F6]" },
  { value: "reservado", label: "Reservado", bg: "bg-[#C0392B]", text: "text-[#F9F9F6]" },
  { value: "vendido", label: "Vendido", bg: "bg-[#1A1A1A]", text: "text-[#F9F9F6]" },
  { value: "bloqueado", label: "Bloqueado", bg: "bg-[#D6D3CB]", text: "text-[#5C5C59]" },
];

const PISO_LABEL = { rc: "R/C Jardim", p1: "1º Piso View", sotao: "2º Piso Rooftop" };

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem(STORAGE_KEY, data.token);
      onLogin(data);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Não foi possível iniciar sessão.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#5C5C59] hover:text-[#2B3B31] transition-colors">
            <ArrowLeft size={14} strokeWidth={1.5} /> Voltar ao site
          </Link>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#9E9789]">ANREÁL · Admin</div>
        </div>
        <div className="bg-white border border-[#E5E5DF] p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={18} strokeWidth={1.25} className="text-[#2B3B31]" />
            <h1 className="font-display text-2xl lg:text-3xl text-[#1A1A1A] tracking-tight">Painel Administrativo</h1>
          </div>
          <p className="text-[13px] text-[#5C5C59] mb-8">Gestão de disponibilidade das fracções.</p>
          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-6">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">Email</label>
              <input
                type="email"
                required
                data-testid="admin-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] outline-none transition-colors"
                placeholder="admin@anrealresidence.pt"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5C59] mb-2">Password</label>
              <input
                type="password"
                required
                data-testid="admin-input-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-[#E5E5DF] focus:border-[#2B3B31] py-3 text-[15px] text-[#1A1A1A] outline-none transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 h-12 px-6 bg-[#2B3B31] text-[#F9F9F6] text-[12px] tracking-[0.24em] uppercase hover:-translate-y-[2px] transition-all duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Entrar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ApartmentsPanel({ token, onLogout }) {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filterBloco, setFilterBloco] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/apartments`);
      setApartments(data);
    } catch (err) {
      toast.error("Não foi possível carregar as fracções.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (apt, newStatus) => {
    if (apt.status === newStatus) return;
    setSavingId(apt.id);
    const prev = apartments.slice();
    setApartments((rows) => rows.map((r) => (r.id === apt.id ? { ...r, status: newStatus } : r)));
    try {
      await axios.patch(`${API}/apartments/${apt.id}/status`, { status: newStatus }, authHeader);
      toast.success(`${apt.bloco}${apt.fraccao} · ${STATUS_OPTIONS.find((s) => s.value === newStatus).label}`);
    } catch (err) {
      setApartments(prev);
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Falha ao atualizar.");
      if (err?.response?.status === 401) onLogout();
    } finally {
      setSavingId(null);
    }
  };

  const filtered = apartments.filter((a) => (filterBloco === "all" || a.bloco === filterBloco) && (filterStatus === "all" || a.status === filterStatus));

  const counts = useMemo(() => {
    const c = { disponivel: 0, reservado: 0, vendido: 0, bloqueado: 0 };
    apartments.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [apartments]);

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <header className="sticky top-0 z-30 bg-[#F9F9F6]/90 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-logo" className="font-display text-[15px] tracking-[0.28em] text-[#1A1A1A]">ANREÁL · ADMIN</Link>
            <div className="hidden md:block text-[11px] tracking-[0.22em] uppercase text-[#9E9789]">Gestão de Fracções</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} aria-label="Atualizar" className="w-10 h-10 flex items-center justify-center border border-[#E5E5DF] hover:bg-white transition-colors">
              <RefreshCcw size={16} strokeWidth={1.5} />
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 h-10 px-4 bg-[#2B3B31] text-[#F9F9F6] text-[11px] tracking-[0.22em] uppercase">
              <LogOut size={14} strokeWidth={1.5} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E5DF] border border-[#E5E5DF] mb-8">
          {STATUS_OPTIONS.map((s) => (
            <div key={s.value} data-testid={`admin-count-${s.value}`} className="bg-[#F9F9F6] p-5">
              <div className={`inline-flex items-center gap-2 px-2 py-1 text-[10px] tracking-[0.22em] uppercase mb-3 ${s.bg} ${s.text}`}>
                <span className="w-1 h-1 rounded-full bg-current" />
                {s.label}
              </div>
              <div className="font-display text-3xl text-[#1A1A1A]">{counts[s.value] || 0}</div>
              <div className="text-[11px] text-[#9E9789] mt-1">fracções</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#9E9789]">Filtro:</div>
          <div className="flex items-center gap-1">
            {["all", "A", "B", "C"].map((b) => (
              <button
                key={b}
                data-testid={`admin-filter-bloco-${b}`}
                onClick={() => setFilterBloco(b)}
                className={`h-9 px-3 text-[11px] tracking-[0.2em] uppercase border transition-colors ${filterBloco === b ? "bg-[#2B3B31] text-[#F9F9F6] border-[#2B3B31]" : "bg-transparent text-[#1A1A1A] border-[#E5E5DF]"}`}
              >
                {b === "all" ? "Todos os Blocos" : `Bloco ${b}`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[{ v: "all", l: "Todos os Estados" }, ...STATUS_OPTIONS.map((s) => ({ v: s.value, l: s.label }))].map((f) => (
              <button
                key={f.v}
                data-testid={`admin-filter-status-${f.v}`}
                onClick={() => setFilterStatus(f.v)}
                className={`h-9 px-3 text-[11px] tracking-[0.2em] uppercase border transition-colors ${filterStatus === f.v ? "bg-[#2B3B31] text-[#F9F9F6] border-[#2B3B31]" : "bg-transparent text-[#1A1A1A] border-[#E5E5DF]"}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-[13px] text-[#5C5C59]">A carregar…</div>
        ) : (
          <div className="border border-[#E5E5DF] bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[#F1F1EC] text-[10px] tracking-[0.22em] uppercase text-[#5C5C59]">
                    <th className="px-4 py-3">Bloco</th>
                    <th className="px-4 py-3">Fracção</th>
                    <th className="px-4 py-3">Piso</th>
                    <th className="px-4 py-3">Tipologia</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Alterar</th>
                  </tr>
                </thead>
                <tbody className="text-[13px]" data-testid="admin-apartments-table">
                  {filtered.map((a) => {
                    const s = STATUS_OPTIONS.find((x) => x.value === a.status) || STATUS_OPTIONS[3];
                    return (
                      <tr key={a.id} data-testid={`admin-row-${a.bloco}-${a.fraccao}`} className="border-t border-[#E5E5DF] hover:bg-[#F9F9F6]/60">
                        <td className="px-4 py-3 font-display text-lg text-[#2B3B31]">Bloco {a.bloco}</td>
                        <td className="px-4 py-3 text-[#1A1A1A] font-medium">{a.fraccao}</td>
                        <td className="px-4 py-3 text-[#5C5C59]">{PISO_LABEL[a.piso] || a.piso}</td>
                        <td className="px-4 py-3 text-[#1A1A1A]">{a.tipologia}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase ${s.bg} ${s.text}`}>
                            <span className="w-1 h-1 rounded-full bg-current" />
                            {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            {STATUS_OPTIONS.map((opt) => {
                              const active = opt.value === a.status;
                              return (
                                <button
                                  key={opt.value}
                                  data-testid={`admin-set-${a.bloco}-${a.fraccao}-${opt.value}`}
                                  onClick={() => updateStatus(a, opt.value)}
                                  disabled={savingId === a.id}
                                  className={`h-7 px-2 text-[10px] tracking-[0.16em] uppercase transition-all disabled:opacity-50 ${active ? `${opt.bg} ${opt.text}` : "border border-[#E5E5DF] text-[#1A1A1A] hover:border-[#2B3B31]"}`}
                                >
                                  {opt.label.slice(0, 3)}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#9E9789]">Nenhuma fracção corresponde ao filtro.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    document.title = "ANREÁL · Admin";
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    let alive = true;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => alive && setChecking(false))
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        if (alive) { setToken(""); setChecking(false); }
      });
    return () => { alive = false; };
  }, [token]);

  const logout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6] text-[13px] text-[#5C5C59]">A verificar sessão…</div>;
  }
  if (!token) return <LoginForm onLogin={(d) => setToken(d.token)} />;
  return <ApartmentsPanel token={token} onLogout={logout} />;
}
